import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { TrendingUp, Megaphone, Target, DollarSign } from 'lucide-react';
import { useCrmPage } from '../../shared/utils/crmPageUtils';
import { getCrmStageLabel } from '../../shared/utils/crmConstants';
import { getRegionLabel } from '../../shared/utils/brandRegions';
import localApiAdapter from '../../core/services/localApiAdapter';
import ModuleAnalyticsSummaryPage from '../../shared/components/analytics/ModuleAnalyticsSummaryPage';
import { buildDashboardCharts } from '../../shared/components/dashboard/moduleDashboardCharts';
import { ALL_CRM_DATA_REFRESH_EVENTS } from './crmRefreshEvents';

function unwrapAnalyticsPayload(response) {
  if (!response || typeof response !== 'object') return null;
  if (response.summary || response.opportunities || response.charts) return response;
  if (response.data && typeof response.data === 'object') return response.data;
  return response;
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

const AnalysesRapportsCrmPage = () => {
  const { t, p, fmtMoney } = useCrmPage('analysesRapportsPage');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await localApiAdapter.get('/crm/analyses-rapports');
      setData(unwrapAnalyticsPayload(response));
    } catch (error) {
      console.error('CRM analytics load error:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshTrigger]);

  useEffect(() => {
    const handler = () => setRefreshTrigger((n) => n + 1);
    ALL_CRM_DATA_REFRESH_EVENTS.forEach((ev) => window.addEventListener(ev, handler));
    return () => ALL_CRM_DATA_REFRESH_EVENTS.forEach((ev) => window.removeEventListener(ev, handler));
  }, []);

  const summary = useMemo(() => {
    const winRateRaw = data?.summary?.winRate
      ?? data?.summary?.conversionRate
      ?? data?.conversionRate
      ?? 0;
    const winRate = Number.isFinite(Number(winRateRaw))
      ? Number(Number(winRateRaw).toFixed(1))
      : 0;

    return {
      pipeline: data?.summary?.pipelineValue ?? data?.opportunities?.pipelineValue ?? 0,
      opportunities: data?.summary?.totalOpportunities ?? data?.opportunities?.total ?? 0,
      winRate,
      campaigns: data?.campaigns?.active ?? 0,
      won: data?.opportunities?.won ?? 0,
      lost: data?.opportunities?.lost ?? 0,
    };
  }, [data]);

  const stages = useMemo(
    () => asArray(data?.opportunities?.byStage || data?.charts?.opportunitiesByStage),
    [data],
  );

  const revenueByRegion = useMemo(
    () => asArray(data?.charts?.revenueByRegion),
    [data],
  );

  const openOpportunities = Math.max(0, summary.opportunities - summary.won - summary.lost);

  const insights = useMemo(() => {
    const topRegion = revenueByRegion[0];
    const pipelinePerOpp = summary.opportunities > 0 ? summary.pipeline / summary.opportunities : 0;

    return [
      { label: p('insight_win_rate'), value: loading ? '…' : `${summary.winRate}%` },
      { label: p('insight_pipeline_per_opp'), value: loading ? '…' : fmtMoney(pipelinePerOpp) },
      {
        label: p('insight_top_region'),
        value: loading ? '…' : getRegionLabel(topRegion?.region) || topRegion?.region || '—',
        hint: topRegion ? fmtMoney(topRegion.total || 0) : undefined,
      },
    ];
  }, [loading, summary, revenueByRegion, p, fmtMoney]);

  const stats = useMemo(
    () => [
      { label: p('stat_pipeline'), value: loading ? '…' : fmtMoney(summary.pipeline), icon: DollarSign, variant: 'forest' },
      { label: p('stat_opportunities'), value: loading ? '…' : summary.opportunities, icon: TrendingUp, variant: 'copper' },
      { label: p('stat_win_rate'), value: loading ? '…' : `${summary.winRate}%`, icon: Target, variant: 'forest' },
      { label: p('stat_campaigns'), value: loading ? '…' : summary.campaigns, icon: Megaphone, variant: 'copper' },
    ],
    [loading, summary, p, fmtMoney],
  );

  const charts = useMemo(() => {
    const pipelineRows = stages.map((row) => ({
      label: getCrmStageLabel(row.stage || row.name, t),
      value: Number(row.count) || 0,
    }));

    const regionRows = revenueByRegion.map((row) => ({
      label: getRegionLabel(row.region) || row.region || '—',
      value: Number(row.total) || 0,
    }));

    return buildDashboardCharts({
      pipeline: {
        title: p('chart_pipeline'),
        subtitle: p('chart_pipeline_subtitle'),
        alwaysShow: true,
        rows: pipelineRows,
      },
      ranked: {
        title: p('chart_revenue'),
        subtitle: p('chart_ranked_subtitle'),
        alwaysShow: true,
        rows: regionRows,
      },
      status: {
        title: p('chart_status_title'),
        subtitle: p('chart_status_subtitle'),
        active: summary.won,
        inactive: openOpportunities,
        alwaysShow: true,
      },
    });
  }, [stages, revenueByRegion, summary.won, openOpportunities, p, t]);

  const table = useMemo(() => {
    const rows = revenueByRegion.map((row) => ({
      region: getRegionLabel(row.region) || row.region || '—',
      total: fmtMoney(row.total || 0),
      count: row.count ?? row.deals ?? '—',
    }));

    return {
      title: p('chart_revenue'),
      columns: [
        { key: 'region', label: t('common.region', { defaultValue: 'المنطقة' }) },
        { key: 'total', label: t('common.amount', { defaultValue: 'المبلغ' }) },
        { key: 'count', label: t('common.count', { defaultValue: 'العدد' }) },
      ],
      rows,
    };
  }, [revenueByRegion, p, t, fmtMoney]);

  return (
    <ModuleAnalyticsSummaryPage
      title={p('title')}
      subtitle={p('subtitle')}
      loading={loading}
      onRefresh={() => setRefreshTrigger((n) => n + 1)}
      stats={stats}
      insights={insights}
      charts={charts}
      table={table}
      exportFilePrefix="crm_analyses"
      emptyLabel={t('common.no_data', { defaultValue: 'لا توجد بيانات' })}
    />
  );
};

export default AnalysesRapportsCrmPage;
